<template>
  <div class="pb-6">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
    </div>

    <div v-else>
      <!-- Filtro discreto -->
      <div v-if="entries.length > 0" class="filter-chips mb-3">
        <button class="filter-chip" :class="{ active: filter === 'all' }" @click="filter = 'all'">Todos</button>
        <button class="filter-chip" :class="{ active: filter === 'credit' }" @click="filter = 'credit'">Recebimentos</button>
        <button class="filter-chip" :class="{ active: filter === 'payout' }" @click="filter = 'payout'">Transferências</button>
        <button class="filter-chip" :class="{ active: filter === 'refund' }" @click="filter = 'refund'">Estornos</button>
      </div>

      <div v-if="filteredEntries.length === 0" class="empty-state text-center py-5">
        <i class="fas fa-receipt fa-2x text-muted mb-3 d-block"></i>
        <p class="text-muted">{{ filter === 'all' ? 'Nenhuma atividade financeira ainda.' : 'Nenhum item para o filtro selecionado.' }}</p>
      </div>

      <div v-else class="ledger-timeline">
        <div
          v-for="entry in filteredEntries"
          :key="entry.id"
          class="ledger-entry"
          :class="[
            `ledger-entry--${getEntryType(entry)}`,
            { 'ledger-entry--expandable': isExpandable(entry) },
            { 'ledger-entry--fee-sub': getEntryType(entry) === 'debit_transfer_fee' }
          ]"
          @click="isExpandable(entry) && toggleEntry(entry.id)"
        >
          <div class="ledger-entry__icon-col">
            <div class="ledger-entry__icon-wrap" :class="`ledger-entry__icon-wrap--${getEntryType(entry)}`">
              <i :class="['fas', entryIcon(entry)]"></i>
            </div>
          </div>

          <div class="ledger-entry__content">
            <div class="ledger-entry__header">
              <span class="ledger-entry__label">{{ entryLabel(entry) }}</span>
              <div class="ledger-entry__header-right">
                <span class="ledger-entry__amount" :class="getAmountClass(entry)">
                  <template v-if="getEntryType(entry) !== 'debit_withdrawal' && getEntryType(entry) !== 'debit_transfer_fee'">
                    {{ entry.net_amount < 0 ? '−' : '+' }}
                  </template>
                  {{ formatCurrency(Math.abs(entry.net_amount)) }}
                </span>
                <i
                  v-if="isExpandable(entry)"
                  class="fas fa-chevron-down ledger-entry__chevron"
                  :class="{ 'ledger-entry__chevron--open': expandedEntries.has(entry.id) }"
                ></i>
              </div>
            </div>

            <div class="ledger-entry__meta">
              <span class="ledger-entry__date">{{ formatDate(entry.created_at) }}</span>
              <span
                class="ledger-entry__badge"
                :class="`ledger-entry__badge--${getEntryType(entry)}`"
              >
                {{ entryStatusLabel(entry) }}
              </span>
            </div>

            <Transition name="ledger-fees">
              <div
                v-if="isExpandable(entry) && expandedEntries.has(entry.id)"
                class="ledger-entry__fees"
              >
                <div class="ledger-entry__fee-row">
                  <span class="text-muted">Valor bruto</span>
                  <span>{{ formatCurrency(entry.gross_amount) }}</span>
                </div>
                <div v-if="(entry.platform_fee + entry.gateway_fee) > 0" class="ledger-entry__fee-row">
                  <span class="text-muted">Taxas do pagamento</span>
                  <span class="text-danger">−{{ formatCurrency(entry.platform_fee + entry.gateway_fee) }}</span>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { formatDate } from '@/utils/dateFormatter';
import { getEntryType, entryIcon, entryLabel, entryStatusLabel, getAmountClass, isExpandable } from '@/utils/ledgerHelpers';

const props = defineProps({
  entries: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  initialFilter: { type: String, default: 'all' },
});

const emit = defineEmits(['update:filter']);

const filter = ref(props.initialFilter);
const expandedEntries = ref(new Set());

watch(filter, (val) => emit('update:filter', val));

const filteredEntries = computed(() => {
  if (filter.value === 'all') return props.entries;
  if (filter.value === 'credit') {
    return props.entries.filter(e => {
      const t = getEntryType(e);
      return t === 'available' || t === 'blocked';
    });
  }
  if (filter.value === 'payout') {
    return props.entries.filter(e => {
      const t = getEntryType(e);
      return t === 'payout' || t === 'payout_pending' || t === 'payout_failed'
          || t === 'debit_withdrawal' || t === 'debit_transfer_fee';
    });
  }
  if (filter.value === 'refund') {
    return props.entries.filter(e => getEntryType(e) === 'refund');
  }
  return props.entries;
});

function toggleEntry(id) {
  if (expandedEntries.value.has(id)) {
    expandedEntries.value.delete(id);
  } else {
    expandedEntries.value.add(id);
  }
  expandedEntries.value = new Set(expandedEntries.value);
}
</script>

<style scoped>
/* ── Filter Chips ── */
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
  user-select: none;
}
.filter-chip:hover {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}
.filter-chip.active {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
  font-weight: 600;
}

/* ── Ledger Timeline ── */
.ledger-timeline { display: flex; flex-direction: column; gap: 0.5rem; }
.ledger-entry {
  display: flex; gap: 0.75rem; background: #fff;
  border-radius: 12px; padding: 0.875rem 1rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.ledger-entry--expandable { cursor: pointer; user-select: none; }
.ledger-entry--expandable:hover { background: #fafafa; }
.ledger-entry--fee-sub {
  margin-top: -6px; padding-top: 0.45rem; padding-bottom: 0.45rem;
  border-top: 1px dashed #f0f0f0; background: #fafafa;
  border-radius: 0 0 12px 12px;
}
.ledger-entry--fee-sub .ledger-entry__icon-wrap { width: 28px; height: 28px; font-size: 0.75rem; }
.ledger-entry--fee-sub .ledger-entry__label { font-size: 0.8rem; color: #9ca3af; }
.ledger-entry--fee-sub .ledger-entry__amount { font-size: 0.82rem; }
.ledger-entry__icon-col { flex-shrink: 0; }
.ledger-entry__icon-wrap {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; font-size: 0.9rem;
}
.ledger-entry__icon-wrap--available  { background: #e8f5e9; color: #388E3C; }
.ledger-entry__icon-wrap--blocked    { background: #fff8e1; color: #f59e0b; }
.ledger-entry__icon-wrap--refund     { background: #fdecea; color: #e53935; }
.ledger-entry__icon-wrap--payout     { background: #e3f2fd; color: #1976D2; }
.ledger-entry__icon-wrap--payout_pending { background: #fff8e1; color: #f59e0b; }
.ledger-entry__icon-wrap--payout_failed  { background: #fdecea; color: #e53935; }
.ledger-entry__icon-wrap--debit_withdrawal { background: #dcfce7; color: #16a34a; }
.ledger-entry__icon-wrap--debit_transfer_fee { background: #f3f4f6; color: #6b7280; }
.ledger-entry__content { flex: 1; min-width: 0; }
.ledger-entry__header {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 0.5rem; margin-bottom: 0.25rem;
}
.ledger-entry__label { font-size: 0.875rem; font-weight: 600; color: #32355d; }
.ledger-entry__amount { font-size: 0.95rem; font-weight: 700; white-space: nowrap; }
.ledger-entry__header-right { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
.ledger-entry__chevron { color: #c0c0c0; font-size: 0.7rem; transition: transform 0.22s ease; }
.ledger-entry__chevron--open { transform: rotate(180deg); }
.ledger-entry__meta { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.4rem; }
.ledger-entry__date { font-size: 0.72rem; color: #c0c0c0; }
.ledger-entry__badge { font-size: 0.65rem; font-weight: 600; padding: 2px 7px; border-radius: 999px; }
.ledger-entry__badge--available { background: #e8f5e9; color: #388E3C; }
.ledger-entry__badge--blocked   { background: #fff8e1; color: #b45309; }
.ledger-entry__badge--refund    { background: #fdecea; color: #c62828; }
.ledger-entry__badge--payout    { background: #e3f2fd; color: #1565C0; }
.ledger-entry__badge--payout_pending { background: #fff8e1; color: #b45309; }
.ledger-entry__badge--payout_failed  { background: #fdecea; color: #c62828; }
.ledger-entry__badge--debit_withdrawal { background: #dcfce7; color: #15803d; }
.ledger-entry__badge--debit_transfer_fee { background: #f3f4f6; color: #6b7280; font-size: 0.6rem; }
.ledger-entry__fees { background: #fafafa; border-radius: 8px; padding: 0.4rem 0.6rem; margin-top: 0.4rem; }
.ledger-entry__fee-row { display: flex; justify-content: space-between; font-size: 0.75rem; padding: 2px 0; }
.ledger-fees-enter-active, .ledger-fees-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.ledger-fees-enter-from, .ledger-fees-leave-to { opacity: 0; transform: translateY(-4px); }

.empty-state i { opacity: 0.4; }
.pb-6 { padding-bottom: 3rem; }
</style>
